import { useState, useEffect } from 'react';
import { Save, GitBranch, Info, Loader2 } from 'lucide-react';
import Modal from '../common/Modal';
import edgeService from '../../services/edge.service';

interface LogicalInterface {
  id: string;
  name: string;
  role: string;
  ipv4?: string;
}

interface TunnelInterface {
  interfaceName: string;
  peeringId: string;
  peeringName: string | null;
  localTunnelIp: string | null;
  peerTunnelIps: string[];
}

interface StaticRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  route?: any;
  mode: 'add' | 'edit';
  edgeId: string;
}

const StaticRouteModal = ({ isOpen, onClose, onSave, route, mode, edgeId }: StaticRouteModalProps) => {
  const [formData, setFormData] = useState({
    destination: '',
    gateway: '',
    interface: '',
    metric: 1,
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [logicalInterfaces, setLogicalInterfaces] = useState<LogicalInterface[]>([]);
  const [tunnelInterfaces, setTunnelInterfaces] = useState<TunnelInterface[]>([]);
  const [interfacesLoading, setInterfacesLoading] = useState(false);

  // Fetch logical + virtual tunnel interfaces when modal opens (VSNP-981)
  useEffect(() => {
    const fetchInterfaces = async () => {
      if (!isOpen || !edgeId) return;

      setInterfacesLoading(true);
      try {
        const [logical, tunnels] = await Promise.all([
          edgeService.listLogicalInterfaces(edgeId),
          edgeService.listTunnelInterfaces(edgeId).catch(() => ({ items: [] })),
        ]);
        setLogicalInterfaces(logical.items || []);
        setTunnelInterfaces(tunnels.items || []);
      } catch (err) {
        console.error('Failed to fetch interfaces:', err);
      } finally {
        setInterfacesLoading(false);
      }
    };

    fetchInterfaces();
  }, [isOpen, edgeId]);

  // Update form data when route prop changes (for edit mode)
  useEffect(() => {
    if (!isOpen) return;

    if (route) {
      // Build destination with CIDR from existing route data
      let destination = route.target_ip || route.destination || '';
      const mask = route.ip_mask || route.subnetMask || '';

      // If destination doesn't have CIDR and we have a mask, append it
      if (destination && !destination.includes('/') && mask) {
        if (mask.startsWith('/')) {
          destination = `${destination}${mask}`;
        } else {
          // Convert subnet mask to CIDR
          const cidr = subnetMaskToCIDR(mask);
          if (cidr) destination = `${destination}${cidr}`;
        }
      }

      setFormData({
        destination,
        gateway: route.next_hop_ip || route.gateway || '',
        interface: route.inter_name || route.interface || '',
        metric: route.metric || 1,
        description: route.description || '',
      });
    } else {
      // Reset form for add mode
      setFormData({
        destination: '',
        gateway: '',
        interface: '',
        metric: 1,
        description: '',
      });
    }
    setError(null);
  }, [route, isOpen]);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Validate CIDR notation
  const validateCIDR = (value: string): boolean => {
    // Match IP/CIDR pattern like 8.8.8.8/32 or 10.0.0.0/8
    const cidrPattern = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
    if (!cidrPattern.test(value)) return false;

    const [ip, prefix] = value.split('/');
    const prefixNum = parseInt(prefix, 10);

    // Validate prefix range
    if (prefixNum < 0 || prefixNum > 32) return false;

    // Validate IP octets
    const octets = ip.split('.').map(Number);
    return octets.every(o => o >= 0 && o <= 255);
  };

  // Validate IPv4 address
  const validateIPv4 = (value: string): boolean => {
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipPattern.test(value)) return false;
    const octets = value.split('.').map(Number);
    return octets.every(o => o >= 0 && o <= 255);
  };

  const handleSubmit = () => {
    // Validate destination has CIDR
    if (!formData.destination) {
      setError('Destination network is required');
      return;
    }

    if (!validateCIDR(formData.destination)) {
      setError('Destination must be in CIDR notation (e.g., 10.0.0.0/24 or 8.8.8.8/32)');
      return;
    }

    // Validate gateway
    if (!formData.gateway) {
      setError('Gateway is required');
      return;
    }

    if (!validateIPv4(formData.gateway)) {
      setError('Gateway must be a valid IPv4 address');
      return;
    }

    // Parse destination into IP and CIDR
    const [targetIp, cidr] = formData.destination.split('/');

    // A virtual tunnel interface (peering wgN) isn't a logical port, so send it
    // as inter_name (not next_hop_port_id, which validates against dev_logical_port).
    // Routing + the WG allowed-ips widening are driven by the gateway/next-hop.
    const isTunnel = tunnelInterfaces.some((t) => t.interfaceName === formData.interface);

    // Transform form data to API format
    const apiData = {
      target_ip: targetIp,
      ip_mask: `/${cidr}`,
      next_hop_ip: formData.gateway,
      next_hop_port_id: isTunnel ? undefined : formData.interface || undefined,
      inter_name: isTunnel ? formData.interface : undefined,
      metric: formData.metric,
      description: formData.description,
    };
    onSave(apiData);
    onClose();
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Route' : 'Save Changes'}</span>
      </button>
    </div>
  );

  // Filter for WAN interfaces (typically used for routing)
  const routingInterfaces = logicalInterfaces.filter(
    (iface) => iface.role === 'WAN' || iface.role === 'LAN'
  );

  // The tunnel interface currently chosen as egress, if any (VSNP-981)
  const selectedTunnel = tunnelInterfaces.find((t) => t.interfaceName === formData.interface);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add Static Route' : 'Edit Static Route'}
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
          <GitBranch className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Static Route</p>
            <p className="text-sm text-blue-700 mt-1">
              Manually define how traffic to specific networks should be routed.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destination Network (CIDR) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.destination}
              onChange={(e) => handleChange('destination', e.target.value)}
              placeholder="e.g., 10.0.0.0/24 or 8.8.8.8/32"
              className="input-base w-full font-mono"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter network address with CIDR prefix (e.g., 192.168.2.0/24)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gateway <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.gateway}
                onChange={(e) => handleChange('gateway', e.target.value)}
                placeholder="e.g., 192.168.1.1"
                className="input-base w-full font-mono"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Next hop IP address</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interface
                {interfacesLoading && <Loader2 className="w-3 h-3 inline ml-2 animate-spin" />}
              </label>
              <select
                value={formData.interface}
                onChange={(e) => handleChange('interface', e.target.value)}
                className="input-base w-full"
                disabled={interfacesLoading}
              >
                <option value="">Auto (based on gateway)</option>
                {routingInterfaces.length > 0 && (
                  <optgroup label="Physical / Logical">
                    {routingInterfaces.map((iface) => (
                      <option key={iface.id} value={iface.name}>
                        {iface.name} ({iface.role}){iface.ipv4 ? ` - ${iface.ipv4}` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {tunnelInterfaces.length > 0 && (
                  <optgroup label="Virtual Tunnels (E2E Peering)">
                    {tunnelInterfaces.map((t) => (
                      <option key={t.peeringId} value={t.interfaceName}>
                        {t.interfaceName}{t.peeringName ? ` — ${t.peeringName}` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {selectedTunnel ? (
                <p className="mt-1 text-xs text-gray-500">
                  Tunnel egress — set Gateway to a peer tunnel IP
                  {selectedTunnel.peerTunnelIps.length > 0
                    ? `: ${selectedTunnel.peerTunnelIps.join(', ')}`
                    : ''}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">Usually auto-detected from gateway</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
              <input
                type="number"
                value={formData.metric}
                onChange={(e) => handleChange('metric', parseInt(e.target.value) || 1)}
                min={1}
                max={255}
                className="input-base w-full"
              />
              <p className="mt-1 text-xs text-gray-500">Lower is preferred (default: 1)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="e.g., Route to LA office"
                className="input-base w-full"
              />
            </div>
          </div>
        </div>

        {/* Route Summary */}
        {formData.destination && formData.gateway && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-medium text-gray-700 mb-2">Route Summary:</p>
            <p className="text-sm text-gray-800 font-mono">
              {formData.destination} via {formData.gateway}
              {formData.interface ? ` dev ${formData.interface}` : ''} metric {formData.metric}
            </p>
          </div>
        )}

        {/* CIDR Reference Panel */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Info className="w-4 h-4 text-gray-500" />
            <p className="text-xs font-medium text-gray-700">CIDR Notation Reference</p>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
            <div className="font-mono">/32</div>
            <div>Single host (255.255.255.255)</div>
            <div className="font-mono">/30</div>
            <div>Point-to-point link (4 IPs)</div>
            <div className="font-mono">/24</div>
            <div>256 IPs (255.255.255.0)</div>
            <div className="font-mono">/16</div>
            <div>65,536 IPs (255.255.0.0)</div>
            <div className="font-mono">/8</div>
            <div>16M IPs (255.0.0.0)</div>
            <div className="font-mono">/0</div>
            <div>Default route (all traffic)</div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Helper to convert subnet mask to CIDR notation
function subnetMaskToCIDR(mask: string): string | null {
  const maskMap: Record<string, string> = {
    '255.255.255.255': '/32',
    '255.255.255.252': '/30',
    '255.255.255.248': '/29',
    '255.255.255.240': '/28',
    '255.255.255.224': '/27',
    '255.255.255.192': '/26',
    '255.255.255.128': '/25',
    '255.255.255.0': '/24',
    '255.255.254.0': '/23',
    '255.255.252.0': '/22',
    '255.255.248.0': '/21',
    '255.255.240.0': '/20',
    '255.255.224.0': '/19',
    '255.255.192.0': '/18',
    '255.255.128.0': '/17',
    '255.255.0.0': '/16',
    '255.254.0.0': '/15',
    '255.252.0.0': '/14',
    '255.248.0.0': '/13',
    '255.240.0.0': '/12',
    '255.224.0.0': '/11',
    '255.192.0.0': '/10',
    '255.128.0.0': '/9',
    '255.0.0.0': '/8',
    '0.0.0.0': '/0',
  };
  return maskMap[mask] || null;
}

export default StaticRouteModal;
